import * as R from "ramda";
import { makeAction } from "async-action-creator";
import { notification } from "antd";

import * as gtag from "../../lib/gtag";
import api from "../../utils/api";
import { createApiThunk } from "../../utils/thunk";
import {
  CANDIDATE_LISTS,
  CANDIDATE_TYPES,
  LIST_BATCH_ACTIONS,
  LIST_BATCH_ACTIONS_TRANSLATIONS,
  FILTERS_FOR
} from "../../constants/opportunity";
import { getCandidateOpportunityStage } from "../../utils/candidates";
import {
  getFiltersForList,
  CANDIDATES_PER_PAGE
} from "../../utils/candidatesFilters";
import i18n from "../../i18n";
import { listHasItems } from "../../utils/arrays";
import {
  getBatchExternalFails,
  getBatchInternalFails,
  getCandidateTypes,
  makeBatchApiRequest,
  retryBatchModal,
  retryBatchInvite,
  retryBatchShare
} from "../../utils/batchActions";
import { pluralsForList } from "../../utils/strings";

const hasDuplicateLikeSignal = error => {
  const responseData = R.path(["response", "data"], error);
  const normalizedData =
    typeof responseData === "string"
      ? responseData.toLowerCase()
      : JSON.stringify(responseData || {}).toLowerCase();

  return /already|exist|duplicate/.test(normalizedData);
};

export const getUpdateCandidateStatusRejectedDescription = ({
  error,
  payload,
  prevState
}) => {
  const status = R.path(["response", "status"], error);
  const candidateOpportunityStage = getCandidateOpportunityStage({
    candidate: payload.candidate,
    selectedCandidates: R.pathOr(
      [],
      ["candidates", "lists", CANDIDATE_LISTS.selected, "candidates"],
      prevState
    )
  });

  if (payload.status === "accepted" && status === 400) {
    if (candidateOpportunityStage === "process") {
      return i18n.t("toasts:candidates.alreadyInProcess");
    }

    if (
      candidateOpportunityStage === "selected" ||
      hasDuplicateLikeSignal(error)
    ) {
      return i18n.t("toasts:candidates.alreadySelected");
    }
  }

  return i18n.t("toasts:candidates.updateCandidateFailed");
};

export const SHARE_CANDIDATE = makeAction("candidates/SHARE_CANDIDATE");

export const SELECT_CANDIDATE = makeAction("candidates/SELECT_CANDIDATE");

export const SELECT_CANDIDATES = makeAction("candidates/SELECT_CANDIDATES");

export const LIST_BATCH_ACTION = makeAction("candidates/LIST_BATCH_ACTION");

export const LIST_BATCH_INVITE = makeAction("candidates/LIST_BATCH_INVITE");

export const CANDIDATES_BATCH_SHARE = makeAction(
  "candidates/CANDIDATES_BATCH_SHARE"
);

export const UPDATE_CANDIDATE_STATUS = makeAction(
  "candidates/UPDATE_CANDIDATE_STATUS"
);

export const FETCH_MATCH_DETAILS = makeAction("candidates/FETCH_MATCH_DETAILS");

export const FETCH_MATCH_MESSAGES = makeAction(
  "candidates/FETCH_MATCH_MESSAGES"
);

export const RESET_SELECTED_CANDIDATE = makeAction(
  "candidates/RESET_SELECTED_CANDIDATE"
);

export const UPDATE_MATCH_NOTES = makeAction("candidates/UPDATE_MATCH_NOTES");
export const CREATE_MATCH_NOTES = makeAction("candidates/CREATE_MATCH_NOTES");

export const FETCH_CANDIDATES_WITH_PAGINATION = makeAction(
  "candidates/FETCH_CANDIDATES_WITH_PAGINATION"
);

export const FETCH_CANDIDATES_WITHOUT_PAGINATION = makeAction(
  "candidates/FETCH_CANDIDATES_WITHOUT_PAGINATION"
);

// TODO refactor this to be nicer
export const SEND_SCREENING_REMINDER = makeAction(
  "candidates/SEND_SCREENING_REMINDER"
);

export const REQUEST_CV_UPDATE = makeAction("candidates/REQUEST_CV_UPDATE");

// Superlike
export const SKIP_SCREENING = makeAction("candidates/SKIP_SCREENING");

export const FETCH_CANDIDATES_BASE = makeAction(
  "candidates/FETCH_CANDIDATES_BASE"
);

export const FETCH_CANDIDATE_BY_ID = makeAction(
  "candidates/FETCH_CANDIDATE_BY_ID"
);

export const UPDATE_CANDIDATE_BY_ID = makeAction(
  "candidates/UPDATE_CANDIDATE_BY_ID"
);

// Multiple candidate select
export const multipleSelectCandidate = ({ candidate, selected }) => ({
  type: "candidates/MULTIPLE_SELECT",
  payload: { candidate, selected }
});

export const resetMultipleSelectCandidates = () => ({
  type: "candidates/RESET_MULTIPLE_SELECT"
});

export const shareCandidate = createApiThunk({
  action: SHARE_CANDIDATE,
  request: ({ candidate, opportunities }, { company }) =>
    Promise.all(
      opportunities.map(opportunity =>
        api.opportunity.candidates.shareCandidate(
          opportunity.id,
          candidate.id,
          company.id
        )
      )
    ),
  onRejected: ({ payload: { opportunities } }) =>
    notification.error({
      message: i18n.t("global:error"),
      description: i18n.t(
        `components:ShareCandidatesModal.${
          opportunities.length > 1
            ? "inviteToOpportunitiesFailed"
            : "inviteToOpportunityFailed"
        }`
      )
    }),
  onResolved: ({ payload: { opportunities } }) =>
    notification.success({
      message: i18n.t("global:good"),
      description: i18n.t(
        `components:ShareCandidatesModal.${
          opportunities.length > 1
            ? "inviteToOpportunitiesSucceeded"
            : "inviteToOpportunitySucceeded"
        }`
      )
    })
});

export const fetchCandidatesWithPagination = createApiThunk({
  passPayloadToError: true,
  action: FETCH_CANDIDATES_WITH_PAGINATION,
  request: async ({ filters, list, endpoint }, { opportunity, company }) => {
    const [{ candidates }, { metadata }] = await Promise.all([
      endpoint(company.id, opportunity.selected.id, {
        filters,
        limit: CANDIDATES_PER_PAGE
      }),
      endpoint(company.id, opportunity.selected.id, {
        filters: R.mergeRight(filters, { registered: "" }),
        limit: 1
      })
    ]);

    return {
      list,
      response: { candidates, metadata }
    };
  }
});

export const fetchCandidatesWithoutPagination = createApiThunk({
  passPayloadToError: true,
  action: FETCH_CANDIDATES_WITHOUT_PAGINATION,
  request: async ({ endpoint, list }, { opportunity, company }) => {
    const response = await endpoint(company.id, opportunity.selected.id, {
      // TODO enable filters, skip, and limit
    });
    // TODO pass response only when there's metadata
    return { list, response: { candidates: response } };
  }
});

const onListBatchActionSuccess = {
  [CANDIDATE_LISTS.suggested]: ({ list, opportunity }) =>
    fetchCandidatesWithPagination({
      filters: getFiltersForList({
        opportunity: opportunity,
        filterKey: `${opportunity.id}_${CANDIDATE_LISTS.suggested}`,
        filtersUsed: FILTERS_FOR[CANDIDATE_LISTS.suggested],
        searchQuery: "",
        onlyServerFilters: true
      }),
      list,
      endpoint: api.opportunity.candidates.fetchSuggested
    }),
  [CANDIDATE_LISTS.hunting]: ({ list, opportunity }) =>
    fetchCandidatesWithPagination({
      filters: getFiltersForList({
        opportunity: opportunity,
        filterKey: `${opportunity.id}_${CANDIDATE_LISTS.suggested}`,
        filtersUsed: FILTERS_FOR[CANDIDATE_LISTS.suggested],
        searchQuery: "",
        onlyServerFilters: true
      }),
      list,
      endpoint: api.opportunity.candidates.fetchHunting
    }),
  [CANDIDATE_LISTS.selected]: ({ list }) =>
    fetchCandidatesWithoutPagination({
      list,
      endpoint: api.opportunity.candidates.fetchSelected
    }),
  [CANDIDATE_LISTS.rejected]: ({ list }) =>
    fetchCandidatesWithoutPagination({
      list,
      endpoint: api.opportunity.candidates.fetchRejected
    })
};

export const listBatchAction = createApiThunk({
  action: LIST_BATCH_ACTION,
  request: async (payload, { opportunity, company }, dispatch) => {
    const { action, candidates } = payload;
    dispatch(resetMultipleSelectCandidates());
    const idCompany = company.id;
    const idOpportunity = opportunity.selected.id;

    const {
      completedScreeningCandidates,
      externalCandidates,
      statusScreeningCandidates
    } = getCandidateTypes(candidates);

    const makeListBatchApiRequest = (candidatesList, type) =>
      makeBatchApiRequest({
        action,
        actions: LIST_BATCH_ACTIONS,
        candidates: candidatesList,
        idCompany,
        idOpportunity,
        type
      });

    const requests = [
      makeListBatchApiRequest(
        externalCandidates,
        CANDIDATE_TYPES.external.type
      ),
      makeListBatchApiRequest(
        completedScreeningCandidates,
        CANDIDATE_TYPES.internal.completedScreening.type
      ),
      makeListBatchApiRequest(
        statusScreeningCandidates,
        CANDIDATE_TYPES.internal.incompleteScreening.type
      )
    ];

    const responses = await Promise.all(requests);

    return { ...payload, responses };
  },
  onResolved: ({ dispatch, getState, payload, response: { responses } }) => {
    const { action, candidates, list } = payload;
    const {
      opportunity: { selected: opportunity }
    } = getState();

    dispatch(onListBatchActionSuccess[list]({ list, opportunity }));

    const [
      externalResponse,
      completedScreeningResponse,
      pendingScreeningResponse
    ] = responses;

    const failedIds = R.flatten([
      getBatchExternalFails(externalResponse),
      getBatchInternalFails(completedScreeningResponse),
      getBatchInternalFails(pendingScreeningResponse)
    ]);

    if (listHasItems(failedIds)) {
      const single = R.not(pluralsForList(candidates));
      const plural = pluralsForList(failedIds);
      retryBatchModal({
        action,
        actions: LIST_BATCH_ACTIONS_TRANSLATIONS,
        allFailed: R.and(
          R.not(single),
          R.eqBy(R.length, failedIds, candidates)
        ),
        candidates,
        failedIds,
        plural,
        single,
        place: i18n.t("opportunity:candidates.multipleSelection.originList"),
        retryCallback: p => dispatch(listBatchAction({ ...payload, ...p }))
      });
    } else {
      notification.success({
        message: i18n.t("global:good"),
        description: i18n.t(
          `opportunity:candidates.multipleSelection.${
            pluralsForList(candidates)
              ? "movedCandidatesTo"
              : "movedCandidateTo"
          }`,
          {
            count: R.length(candidates),
            action: LIST_BATCH_ACTIONS_TRANSLATIONS[action]()
          }
        )
      });
    }
  },
  onRejected: ({ dispatch, payload }) => {
    const { action, candidates } = payload;
    retryBatchModal({
      action,
      actions: LIST_BATCH_ACTIONS_TRANSLATIONS,
      allFailed: pluralsForList(candidates),
      candidates,
      failedIds: R.map(R.prop("id"), candidates),
      plural: pluralsForList(candidates),
      single: R.not(pluralsForList(candidates)),
      place: i18n.t("opportunity:candidates.multipleSelection.originList"),
      retryCallback: p => dispatch(listBatchAction({ ...payload, ...p }))
    });
  },
  passPayloadToError: true
});

// This action is to anything thats related to multiple invites, it can also be adjusted and scaled to send screening reminders
export const listBatchInvite = createApiThunk({
  action: LIST_BATCH_INVITE,
  request: async (payload, { opportunity, company }, dispatch) => {
    dispatch(resetMultipleSelectCandidates());
    const { action, candidates, type } = payload;
    const idCompany = company.id;
    const idOpportunity = opportunity.selected.id;

    const response = await makeBatchApiRequest({
      action,
      actions: LIST_BATCH_ACTIONS,
      candidates,
      idCompany,
      idOpportunity,
      emptyBody: true,
      type
    });

    return { ...payload, response };
  },
  onResolved: ({ dispatch, payload, response: { response } }) => {
    const { action, candidates } = payload;
    const failedIds = getBatchExternalFails(response);

    if (listHasItems(failedIds)) {
      const single = R.not(pluralsForList(candidates));
      const plural = pluralsForList(failedIds);
      retryBatchInvite({
        action,
        actions: LIST_BATCH_ACTIONS_TRANSLATIONS,
        allFailed: R.and(
          R.not(single),
          R.eqBy(R.length, failedIds, candidates)
        ),
        candidates,
        failedIds,
        plural,
        single,
        retryCallback: p => dispatch(listBatchInvite({ ...payload, ...p }))
      });
    } else {
      notification.success({
        message: i18n.t("global:good"),
        description: i18n.t(
          `opportunity:candidates.multipleSelection.${
            pluralsForList(candidates)
              ? "invitedCandidates"
              : "invitedCandidate"
          }`,
          {
            count: R.length(candidates),
            action: LIST_BATCH_ACTIONS_TRANSLATIONS[action]()
          }
        )
      });
    }
  },
  onRejected: ({ dispatch, payload }) => {
    const { action, candidates } = payload;
    retryBatchInvite({
      action,
      actions: LIST_BATCH_ACTIONS_TRANSLATIONS,
      allFailed: true,
      candidates,
      failedIds: R.map(R.prop("id"), candidates),
      plural: pluralsForList(candidates),
      single: R.not(pluralsForList(candidates)),
      retryCallback: p => dispatch(listBatchInvite({ ...payload, ...p }))
    });
  },
  passPayloadToError: true
});

export const candidatesBatchShare = createApiThunk({
  action: CANDIDATES_BATCH_SHARE,
  request: async (payload, { company }, dispatch) => {
    dispatch(resetMultipleSelectCandidates());
    const { action, candidates, opportunities } = payload;
    const idCompany = company.id;

    const makeShareBatchApiRequest = idOpportunity =>
      makeBatchApiRequest({
        action,
        actions: LIST_BATCH_ACTIONS,
        candidates,
        idCompany,
        idOpportunity,
        // We treat all candidates as internal in this request because this operation is beyond moving candidates in a list and wasn't expected to be used with external candidates
        type: CANDIDATE_TYPES.internal.type
      });

    const requests = R.map(
      R.compose(
        makeShareBatchApiRequest,
        R.prop("id")
      ),
      opportunities
    );

    const responses = await Promise.all(requests);

    return { ...payload, responses };
  },
  onResolved: ({ dispatch, payload, response: { responses } }) => {
    const { candidates, opportunities } = payload;

    const failedResponses = R.compose(
      R.reject(
        R.compose(
          R.isEmpty,
          R.prop("fails")
        )
      ),
      R.flatten,
      R.addIndex(R.map)((response, opportunityIndex) => [
        {
          opportunity: opportunities[opportunityIndex],
          fails: getBatchInternalFails(response)
        }
      ])
    )(responses);

    if (listHasItems(failedResponses)) {
      retryBatchShare({
        plural: pluralsForList(candidates) || pluralsForList(opportunities),
        retryCallback: () =>
          dispatch(
            candidatesBatchShare({
              ...payload,
              opportunities: R.map(R.prop("opportunity"), failedResponses)
            })
          )
      });
    } else {
      notification.success({
        message: i18n.t("global:good"),
        description: i18n.t(
          `components:ShareCandidatesModal.${
            pluralsForList(candidates) || pluralsForList(opportunities)
              ? "inviteToOpportunitiesSucceeded"
              : "inviteToOpportunitySucceeded"
          }`
        )
      });
    }
  },
  onRejected: ({ dispatch, payload }) => {
    const { candidates, opportunities } = payload;
    retryBatchShare({
      plural: pluralsForList(candidates) || pluralsForList(opportunities),
      retryCallback: () => dispatch(candidatesBatchShare({ ...payload }))
    });
  }
});

export const updateCandidateStatus = createApiThunk({
  action: UPDATE_CANDIDATE_STATUS,
  request: async (payload, { opportunity, company }) => {
    const { candidate, status } = payload;
    const companyId = company.id;
    const opportunityId = opportunity.selected.id;
    // TODO change propOr to prop
    const isRegistered = R.propOr(true, "isRegistered")(candidate);

    const update =
      status === "accepted"
        ? isRegistered
          ? api.opportunity.candidates.like
          : api.opportunity.candidates.externalLike
        : isRegistered
        ? api.opportunity.candidates.dislike
        : api.opportunity.candidates.externalDislike;

    await update(companyId, opportunityId, candidate.id);

    return payload;
  },
  onResolved: ({ payload: { status, description, message } }) => {
    notification.success({
      message,
      description
    });
    gtag.event({
      category: "Candidates",
      action: status === "accepted" ? "Liked candidate" : "Rejected candidate"
    });
  },
  onRejected: ({ error, payload, prevState }) => {
    notification.error({
      message: i18n.t("global:error"),
      description: getUpdateCandidateStatusRejectedDescription({
        error,
        payload,
        prevState
      })
    });
  },
  passPayloadToError: true
});

export const moveCandidateToProcess = createApiThunk({
  action: UPDATE_CANDIDATE_STATUS,
  request: async (payload, { opportunity, company }) => {
    const { candidate } = payload;

    await api.opportunity.candidates.moveToProcess(
      company.id,
      opportunity.selected.id,
      candidate.id
    );

    return {
      ...payload,
      status: "accepted",
      explicitMoveToProcess: true
    };
  },
  onResolved: ({ payload: { message, description } }) => {
    notification.success({
      message,
      description
    });
    gtag.event({
      category: "Candidates",
      action: "Moved candidate to process"
    });
  },
  onRejected: () => {
    notification.error({
      message: "Error",
      description: "Hubo un error al mover el candidato al proceso."
    });
  },
  passPayloadToError: true
});

export const fetchMatchDetails = createApiThunk({
  action: FETCH_MATCH_DETAILS,
  request: async ({ candidateId, isRegistered }, { opportunity, company }) => {
    const request = isRegistered
      ? api.opportunity.candidates.fetchMatch
      : api.opportunity.candidates.fetchExternalMatch;
    return request(company.id, opportunity.selected.id, candidateId);
  }
});

export const fetchCandidateDetails = createApiThunk({
  action: FETCH_MATCH_DETAILS,
  request: async (candidateId, { opportunity, company }) =>
    api.opportunity.candidates.fetchCandidate(
      company.id,
      opportunity.selected.id,
      candidateId
    )
});

export const updateMatchNotes = createApiThunk({
  action: UPDATE_MATCH_NOTES,
  request: async (
    { idStep, id, formData, opportunityId },
    { opportunity, company }
  ) =>
    await api.opportunity.candidates.updateMatchNotes(
      company.id,
      opportunityId || opportunity.selected.id,
      idStep,
      id,
      formData
    ),

  onResolved: () =>
    notification.success({
      message: i18n.t("global:good"),
      description: i18n.t("toasts:notes.success")
    }),
  onRejected: () =>
    notification.error({
      message: i18n.t("global:error"),
      description: i18n.t("toasts:notes.error")
    })
});

export const createMatchNotes = createApiThunk({
  action: CREATE_MATCH_NOTES,
  request: async (
    { idStep, formData, opportunityId },
    { opportunity, company }
  ) =>
    await api.opportunity.candidates.createMatchNote(
      company.id,
      opportunityId || opportunity.selected.id,
      idStep,
      formData
    ),

  onResolved: () =>
    notification.success({
      message: i18n.t("global:good"),
      description: i18n.t("toasts:notes.success")
    }),
  onRejected: () =>
    notification.error({
      message: i18n.t("global:error"),
      description: i18n.t("toasts:notes.error")
    })
});

export const fetchMatchMessages = createApiThunk({
  action: FETCH_MATCH_MESSAGES,
  request: async (candidateId, { opportunity, company }) =>
    api.messages.fetchMessages(company.id, candidateId, opportunity.selected.id)
});

export const sendScreeningReminder = createApiThunk({
  action: SEND_SCREENING_REMINDER,
  request: async (candidateId, { opportunity, company }) => {
    await api.opportunity.candidates.sendScreeningReminder(
      company.id,
      opportunity.selected.id,
      candidateId
    );
    return candidateId;
  },
  onResolved: () =>
    notification.success({
      message: i18n.t("global:good"),
      description: i18n.t("toasts:candidates.sendScreeningReminderResolved")
    }),
  onRejected: () =>
    notification.error({
      message: i18n.t("global:error"),
      description: i18n.t("toasts:candidates.sendScreeningReminderRejected")
    })
});

export const requestCvUpdate = createApiThunk({
  passPayloadToError: true,
  action: REQUEST_CV_UPDATE,
  request: async (candidateId, { opportunity, company }) => {
    await api.opportunity.candidates.requestCvUpdate(
      company.id,
      opportunity.selected.id,
      candidateId
    );

    return candidateId;
  },
  onResolved: () =>
    notification.success({
      message: i18n.t("global:good"),
      description: i18n.t("toasts:candidates.requestCvUpdateResolved")
    }),
  onRejected: () =>
    notification.error({
      message: i18n.t("global:error"),
      description: i18n.t("toasts:candidates.requestCvUpdateRejected")
    })
});

export const skipScreening = createApiThunk({
  passPayloadToError: true,
  action: SKIP_SCREENING,
  request: async (payload, { opportunity, company }) => {
    const { candidate } = payload;
    const candidateId = candidate.id;
    const companyId = company.id;
    const opportunityId = opportunity.selected.id;

    const response = await api.opportunity.candidates.superlike(
      companyId,
      opportunityId,
      candidateId
    );

    return { response, payload };
  },
  onResolved: () =>
    notification.success({
      message: i18n.t("global:good"),
      description: i18n.t("toasts:candidates.skipScreeningResolved")
    }),
  onRejected: () =>
    notification.error({
      message: i18n.t("global:error"),
      description: i18n.t("toasts:candidates.skipScreeningRejected")
    })
});

export const fetchCandidatesBase = createApiThunk({
  action: FETCH_CANDIDATES_BASE,
  request: async ({ filters, limit = CANDIDATES_PER_PAGE }, { company }) => {
    const [{ candidates }, { metadata }] = await Promise.all([
      api.opportunity.candidates.fetchCandidatesBase(company.id, {
        limit,
        filters
      }),
      api.opportunity.candidates.fetchCandidatesBase(company.id, {
        limit,
        filters: R.mergeRight(filters, { registered: "" })
      })
    ]);

    return { candidates, metadata };
  }
});

export const fetchCandidateBaseByID = createApiThunk({
  action: FETCH_CANDIDATE_BY_ID,
  request: async ({ id }, { company }) =>
    await api.candidate.fetchCandidateByID(id, company.id)
});

export const updateCandidateBaseByID = createApiThunk({
  action: UPDATE_CANDIDATE_BY_ID,
  request: async ({ id, formData }, { company }) =>
    await api.candidate.updateCandidateByID(id, company.id, formData),
  onResolved: () =>
    notification.success({
      message: i18n.t("global:good"),
      description: i18n.t("toasts:candidates.updateCandidateSuccess")
    }),
  onRejected: () =>
    notification.error({
      message: i18n.t("global:error"),
      description: i18n.t("toasts:candidates.updateCandidateFailed")
    })
});
